<script setup>
import { computed, onMounted, ref } from "vue";
import { useScrollStore } from "@/stores/storeScroll";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { useModals } from "@/composables/useModals";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";
import BaseButtonExchange from "@/components/ui/BaseButtonExchange.vue";
import { useRouter } from 'vue-router';

const router = useRouter();

const goToSection = () => {
    router.push({ name: 'profile', hash: '#prizesCase' });
    // или
    router.push('/profile#prizesCase');
};

const storeScroll = useScrollStore();
const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();
const { openModal } = useModals();

const leaves = ref(null);
const isShowModal = ref(false);

const classIcon = computed(() => (isShowModal.value ? "leaf --x" : "leaf --green"));

const openModalLeaves = () => {
	isShowModal.value = !isShowModal.value;
};

const clickBtnProfile = () => {
	if (storeSettings.statusStockFuture) {
		openModal("promoNotStart");
	}

	if (storeSettings.statusStockPast || storeSettings.statusStockActive) {
		openModal("authorizationRequired");
	}
	// sendMetrik("reachGoal", "klik_button_profile");
};

onMounted(() => {
	storeScroll.fixedButton = leaves.value;
});
</script>
<template>
	<div :class="['leaves', { '--hidden': !storeScroll.isFixedButton }]" ref="leaves">
		<Transition>
			<div class="modal-leaves" v-if="isShowModal">
				<h5 class="modal-leaves__title">Мои листочки</h5>
				<div class="modal-leaves__count" v-if="storeRequests.isAuthenticated">
					<span>{{ storeRequests.userData?.points_balance || 0  }}</span>
					<svg width="32" height="32"><use href="#leaf"></use></svg>
				</div>
				<p class="modal-leaves__text" v-if="storeRequests.isAuthenticated">
					Чтобы заработать листочки, <br />
					загружайте чеки.
				</p>
				<p class="modal-leaves__text" v-if="storeRequests.isAuthenticated">Каждый рубль, потраченный на покупку любого чая Greenfield, равен 1 листочку.</p>
				<p class="modal-leaves__text" v-else>Для отображения баланса листочков авторизуйтесь или зарегистрируйтесь.</p>
				<div class="modal-leaves__desc" v-if="storeRequests.isAuthenticated">
					<span class="white">1 рубль</span> =
					<span class="yellow">
						1 <svg width="24" height="24"><use href="#leaf"></use></svg>
					</span>
				</div>
				<div class="modal-leaves__btns" v-if="storeRequests.isAuthenticated">
					<BaseButtonUploadReceipt type-btn="upload_check_3_popup" class="modal-leaves__btn ">
						<svg width="24" height="24"><use href="#download"></use></svg>
						Загрузить чек
					</BaseButtonUploadReceipt>
					<BaseButtonExchange @click="goToSection" class="modal-leaves__btn " :icon="true" title="Обменять" />
				</div>
				<div class="modal-leaves__btns" v-else>
					<button class="modal-leaves__btn btn " @click="clickBtnProfile">Войти</button>
					<button v-if="storeSettings.statusStockActive" class="modal-leaves__btn btn " @click="openModal('registration')">Регистрация</button>
				</div>
			</div>
		</Transition>
		<Transition name="fade">
			<button class="leaves__btn btn" @click="openModalLeaves" v-if="storeScroll.isFixedButton">
				<svg :class="[classIcon]" width="32" height="32"><use :href="isShowModal ? '#x' : '#leaf'"></use></svg>
			</button>
		</Transition>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.leaves {
	position: fixed;
	bottom: 4rem;
	right: 7.5rem;
	z-index: 2;
	display: grid;
	gap: 1rem;
	justify-items: end;

	@media (max-width: vars.$t) {
		right: 2rem;
	}

	@media (max-width: vars.$x480) {
		right: 1rem;
	}
	&.--hidden {
		opacity: 0;
		transition: var(--transition) ease-in-out;
	}
	&__btn {
		display: grid;
		place-content: center;
		width: 8rem;
		height: 8rem;
		padding: 0;
		@media (max-width: vars.$x480) {
			width: 4.8rem;
			height: 4.8rem;
		}
		svg {
			width: 4rem;
			height: 4rem;
			@media (max-width: vars.$x480) {
				width: 3.2rem;
				height: 3.2rem;
			}
		}
	}
}
.modal-leaves {
	display: grid;
	gap: 1.8rem;
	border: 1px solid var(--border-accent-2-3);
	border-radius: 2.4rem;
	padding: 3rem 3.2rem 2rem 3.2rem;
	width: 40.8rem;
	backdrop-filter: blur(15.399999618530273px);
	box-shadow: inset 0 13px 35px 0 rgba(255, 255, 255, 0.14);
	background: var(--fill-accent-1-1);
	@media (max-width: vars.$x480) {
		width: 36rem;
	}
	@media (max-width: vars.$x360) {
		width: 33.5rem;
	}
	&__title {
		font-family: var(--second-family);
		font-weight: 700;
		font-size: 2rem;
		line-height: 140%;
		text-transform: uppercase;
		color: var(--text-inverse-100);
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
		}
	}
	&__count {
		display: flex;
		align-items: end;
		gap: 5px;
		color: var(--text-accent-2-100);
		span {
			font-family: var(--second-family);
			font-weight: 500;
			font-size: 2.4rem;
			line-height: 100%;
			text-transform: uppercase;
		}
		svg {
			width: 3.2rem;
			height: 3.2rem;
			fill: currentColor;
		}
	}

	&__text {
		font-weight: 400;
		font-size: 1.6rem;
		line-height: 140%;
		color: var(--text-inverse-100);
	}

	&__desc {
		font-weight: 500;
		font-size: 2rem;
		line-height: 140%;
		color: var(--text-inverse-100);
		.white {
		}
		.yellow {
			display: inline-flex;
			gap: 3px;
			color: var(--text-accent-2-100);
		}

		svg {
			width: 2.4rem;
			height: 2.4rem;
			fill: currentColor;
		}
	}
	&__btns {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
	&__btn {
		min-height: 4.8rem;
		border-radius: 1.6rem;
		font-size: 2rem;
		line-height: 120%;
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
			min-height: 4rem;
		}
		svg {
			width: 2.4rem;
			height: 2.4rem;
		}
		:deep(.leaf) {
			width: 2.4rem;
			height: 2.4rem;
		}
	}
}
.v-enter-active,
.v-leave-active {
	transition:
		opacity 0.4s ease,
		transform 0.4s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
